/**
 * /ndt-inspection/welds/:id — one weld and its exams (GET
 * /ndt-inspection/welds/:id, fieldiq-api#1144).
 *
 * EXAMS ARE GROUPED BY REPAIR CYCLE — original, R1, R2, cut-out — each exam
 * with its result, who shot it at what level (the API's snapshot), the
 * instrument, the source and its activity decayed to the exam day (the API's
 * figure, in meta.units.activity), and its indications.
 *
 * Recording an exam goes through THE SHOT GUARD on the API. A refusal (409)
 * comes back with every failure and is shown in the dialog; a save may carry
 * warnings (a cert, calibration or leak test due soon), shown here after it.
 *
 * The welder-qualification check is a WARNING — the badge and its reasons are
 * shown, and nothing is blocked on it.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardCheck, Hammer, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteNdtExam, useDeleteNdtWeld, useNdtJob, useNdtWeld } from '../../hooks/useNdtInspection';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canWorkNdt,
  formatNdtActivity,
  formatNdtValue,
  ndtCycleLabel,
  ndtExamResultSkin,
  ndtLevelLabel,
  ndtQualStatusSkin,
  ndtRefusal,
  ndtWeldStandingSkin,
} from '../../lib/ndt-inspection';
import { NDT_EXAM_CYCLES, type NdtExam, type NdtMeta } from '../../types/ndt-inspection';
import { useBackToList } from '../downhole/recordPage';
import { ConfirmDialog } from './NdtDialogs';
import { isNotFound, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtLoading, NdtPanel, NdtSpec, ndtDay } from './NdtBits';
import { NdtQualBadge, NdtWarnings } from './NdtJobBits';
import { NdtExamDialog, NdtWeldDialog } from './NdtJobDialogs';

/** The exams in cycle order; any cycle the API adds later goes last rather than vanishing. */
export function examsByCycle(exams: NdtExam[]): { cycle: string; exams: NdtExam[] }[] {
  const order = [...NDT_EXAM_CYCLES, ...Array.from(new Set(exams.map((e) => e.cycle))).filter((c) => !(NDT_EXAM_CYCLES as readonly string[]).includes(c))];
  return order.map((cycle) => ({ cycle, exams: exams.filter((e) => e.cycle === cycle) })).filter((g) => g.exams.length > 0);
}

function ExamCard({ exam, meta, onDelete }: { exam: NdtExam; meta: NdtMeta | undefined; onDelete?: () => void }) {
  const units = meta?.units ?? null;
  const isRt = exam.method === 'RT';
  return (
    <div data-testid="ndt-exam" className={`rounded-fi-panel border border-l-4 border-fieldiq-border bg-fieldiq-dark/30 ${ndtExamResultSkin(exam.result).rail}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-extrabold text-white">{exam.methodLabel || exam.method}</span>
          {exam.techniqueLabel && <span className="text-xs text-fieldiq-muted">{exam.techniqueLabel}</span>}
          <NdtBadge skin={ndtExamResultSkin(exam.result)} value={exam.result} />
        </div>
        {onDelete && (
          <button type="button" className={NDT_SECONDARY} aria-label={`Delete ${exam.method} ${exam.cycle} exam`} onClick={onDelete}>
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <dl className="grid gap-3 border-t border-fieldiq-border px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
        <NdtSpec label="Technician" value={`${exam.technicianName || '—'} · ${ndtLevelLabel(exam.technicianLevel)}`} />
        <NdtSpec label="Examined" value={formatInstant(exam.examinedAt, meta?.timeZone) ?? ndtDay(exam.examDay)} />
        <NdtSpec label="Instrument" value={exam.instrumentName || '—'} />
        {exam.repairWelderName && <NdtSpec label="Repair welder" value={exam.repairWelderName} />}
        {isRt && (
          <>
            <NdtSpec label="Source" value={exam.sourceSerial ? `${exam.sourceSerial} · ${exam.sourceIsotope ?? ''}` : 'None (X-ray)'} />
            <NdtSpec label="Activity on the day" value={formatNdtActivity(exam.sourceActivity, units?.activity)} mono />
            <NdtSpec label="Shots / films" value={`${exam.shotsCount ?? '—'} / ${exam.filmsCount ?? '—'}`} mono />
            <NdtSpec label="Density · IQI · sens." value={`${formatNdtValue(exam.filmDensity, null)} · ${exam.iqi || '—'} · ${formatNdtValue(exam.sensitivityPercent, '%')}`} />
          </>
        )}
      </dl>
      {exam.indications.length > 0 && (
        <div className="overflow-x-auto border-t border-fieldiq-border">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-2">Indication</th>
                <th scope="col" className="px-4 py-2">Disposition</th>
                <th scope="col" className="px-4 py-2">Location</th>
                <th scope="col" className="px-4 py-2">Clock</th>
                <th scope="col" className="px-4 py-2">Length</th>
                <th scope="col" className="px-4 py-2">Depth</th>
              </tr>
            </thead>
            <tbody>
              {exam.indications.map((i) => (
                <tr key={i.id} data-testid="ndt-indication" className="border-t border-fieldiq-border text-fieldiq-text">
                  <td className="px-4 py-2">{i.indicationTypeLabel || i.indicationType}</td>
                  <td className="px-4 py-2">{i.disposition === 'reject' ? 'Reject' : i.disposition === 'accept' ? 'Accept' : '—'}</td>
                  <td className="px-4 py-2 font-mono">{formatNdtValue(i.location, units?.length)}</td>
                  <td className="px-4 py-2 font-mono">{i.clockPosition ?? '—'}</td>
                  <td className="px-4 py-2 font-mono">{formatNdtValue(i.length, units?.length)}</td>
                  <td className="px-4 py-2 font-mono">{formatNdtValue(i.depth, units?.length)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {exam.notes && <p className="border-t border-fieldiq-border px-4 py-2 text-sm text-fieldiq-text">{exam.notes}</p>}
    </div>
  );
}

export default function WeldDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList('/ndt-inspection/welds');
  const { user } = useAuth();
  const query = useNdtWeld(id);
  const weld = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units = meta?.units ?? null;
  const jobQuery = useNdtJob(weld?.jobId);
  const job = jobQuery.data?.data ?? null;
  const removeExam = useDeleteNdtExam();
  const removeWeld = useDeleteNdtWeld();
  const [examOpen, setExamOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<{ kind: 'exam'; exam: NdtExam } | { kind: 'weld' } | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const jobOpen = !!job && (job.status === 'scheduled' || job.status === 'active');
  const canWork = canWorkNdt(user?.role) && jobOpen;
  const exams = weld && 'exams' in weld ? weld.exams : [];
  const qual = weld?.welderQualification;

  const doConfirm = () => {
    if (!confirm || !weld) return;
    setConfirmError(null);
    if (confirm.kind === 'exam') {
      removeExam.mutate(confirm.exam.id, {
        onSuccess: () => {
          toast.success('Exam deleted');
          setConfirm(null);
        },
        onError: (err) => setConfirmError(ndtRefusal(err, 'The exam could not be deleted.').message),
      });
    } else {
      removeWeld.mutate(weld.id, {
        onSuccess: () => {
          toast.success(`Weld ${weld.weldNumber} deleted`);
          back();
        },
        onError: (err) => setConfirmError(ndtRefusal(err, 'The weld could not be deleted.').message),
      });
    }
  };

  return (
    <div data-testid="ndt-weld-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={weld?.jobNumber ? `NDT inspection · ${weld.jobNumber}` : 'NDT inspection · Weld'}
        title={weld ? `Weld ${weld.weldNumber}` : query.isLoading ? 'Weld' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          canWork ? (
            <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setExamOpen(true)} disabled={!meta?.units}>
              <Plus className="size-4" aria-hidden="true" />
              Record exam
            </button>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: 'Standing', tone: 'neutral', loading: query.isLoading, value: weld ? ndtWeldStandingSkin(weld.standing).label : '—' },
          { icon: <ShieldCheck className="size-4" aria-hidden="true" />, label: 'Welder qual', tone: qual?.status === 'qualified' ? 'success' : qual?.status === 'not_qualified' ? 'accent' : 'neutral', loading: query.isLoading, value: qual ? ndtQualStatusSkin(qual.status).label : '—' },
          { icon: <Hammer className="size-4" aria-hidden="true" />, label: 'Exams', tone: 'neutral', loading: query.isLoading, value: weld ? weld.examCount : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading weld" />
          ) : notFound || (!failure && !weld) ? (
            <NdtPanel eyebrow="Weld" title="Weld">
              <NdtEmpty>This weld was not found. It may have been deleted.</NdtEmpty>
            </NdtPanel>
          ) : failure || !weld ? (
            <NdtPanel eyebrow="Weld" title="Weld">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this weld" />
            </NdtPanel>
          ) : (
            <>
              <NdtWarnings warnings={warnings} />
              <NdtPanel
                eyebrow="Weld"
                title="Details"
                actions={
                  canWork ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setEditing(true)}>
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit
                    </button>
                  ) : undefined
                }
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <NdtSpec label="Job" value={<Link className="underline" to={`/ndt-inspection/jobs/${weld.jobId}`}>{weld.jobNumber || '—'}</Link>} />
                  <NdtSpec label="Line / spool" value={[weld.lineRef, weld.spool].filter(Boolean).join(' · ') || '—'} />
                  <NdtSpec label="Station / KP" value={[weld.station, weld.kp].filter(Boolean).join(' · ') || '—'} />
                  <NdtSpec label="Joint" value={weld.jointTypeLabel || '—'} />
                  <NdtSpec label="Diameter" value={formatNdtValue(weld.diameter, units?.length)} mono />
                  <NdtSpec label="Wall" value={formatNdtValue(weld.wall, units?.length)} mono />
                  <NdtSpec label="Material" value={weld.material || '—'} />
                  <NdtSpec label="Process / position" value={[weld.processLabel || weld.process, weld.position].filter(Boolean).join(' · ') || '—'} />
                  <NdtSpec label="Welded" value={ndtDay(weld.weldDate)} />
                  <NdtSpec label="External ref" value={weld.externalWeldRef || '—'} />
                  <NdtSpec label="Weld UID" value={weld.weldUid || '—'} mono />
                </dl>
                {weld.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{weld.notes}</p>}
              </NdtPanel>

              <NdtPanel eyebrow="Welder" title={weld.welderName ? `${weld.welderName}${weld.welderStamp ? ` (${weld.welderStamp})` : ''}` : 'No welder recorded'} testId="ndt-weld-welder">
                <div className="space-y-2 px-4 py-4">
                  <NdtQualBadge status={qual?.status} reasons={qual?.reasons} />
                  {qual?.checkedOn && <p className="text-xs text-fieldiq-muted">Checked against {ndtDay(qual.checkedOn)}.</p>}
                  {weld.welderId && (
                    <Link className="text-sm underline" to={`/ndt-inspection/welders/${weld.welderId}`}>
                      Open the welder&apos;s qualifications
                    </Link>
                  )}
                </div>
              </NdtPanel>

              <NdtPanel eyebrow="Exams" title="By repair cycle" testId="ndt-weld-exams">
                {exams.length === 0 ? (
                  <NdtEmpty>No exam is recorded on this weld.</NdtEmpty>
                ) : (
                  <div className="space-y-5 px-4 py-4">
                    {examsByCycle(exams).map((g) => (
                      <section key={g.cycle} aria-labelledby={`cycle-${g.cycle}`} data-testid="ndt-exam-cycle">
                        <h3 id={`cycle-${g.cycle}`} className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                          {ndtCycleLabel(g.cycle) === '—' ? g.cycle : ndtCycleLabel(g.cycle)}
                        </h3>
                        <div className="space-y-3">
                          {g.exams.map((e) => (
                            <ExamCard
                              key={e.id}
                              exam={e}
                              meta={meta}
                              onDelete={
                                canWork
                                  ? () => {
                                      setConfirmError(null);
                                      setConfirm({ kind: 'exam', exam: e });
                                    }
                                  : undefined
                              }
                            />
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </NdtPanel>

              {canWork && weld.examCount === 0 && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    className={NDT_SECONDARY}
                    onClick={() => {
                      setConfirmError(null);
                      setConfirm({ kind: 'weld' });
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                    Delete weld
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {weld && canWork && (
        <NdtExamDialog
          open={examOpen}
          onClose={() => setExamOpen(false)}
          weldId={weld.id}
          crew={job?.crew ?? null}
          meta={meta}
          onSaved={setWarnings}
        />
      )}
      {weld && canWork && <NdtWeldDialog open={editing} onClose={() => setEditing(false)} jobId={weld.jobId} weld={weld} units={units} />}
      {weld && canWork && (
        <ConfirmDialog
          open={!!confirm}
          title={confirm?.kind === 'exam' ? 'Delete exam' : 'Delete weld'}
          body={
            confirm?.kind === 'exam'
              ? `Delete the ${confirm.exam.method} ${ndtCycleLabel(confirm.exam.cycle)} exam? Only the latest cycle of a method can be deleted.`
              : `Delete weld ${weld.weldNumber}? It has no exams.`
          }
          confirmLabel="Delete"
          pending={removeExam.isPending || removeWeld.isPending}
          error={confirmError}
          onClose={() => setConfirm(null)}
          onConfirm={doConfirm}
        />
      )}
    </div>
  );
}
